import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { GradingGrid } from '@/components/common/grading-grid';
import { GradingGridRow } from '@/components/common/grading-grid/types';
import {
  defaultGradingRows,
  gridToStageGradeCount,
  sumGradingRows,
} from '@/components/common/grading-grid/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type AddMissingStrawsDialogProps = {
  visible: boolean;
  onHide: () => void;
  onConfirm: (
    entries: { count: number; stage?: number; grade?: number }[],
  ) => Promise<void | ((count: number) => void)>;
};

export const AddMissingStrawsDialog = ({
  visible,
  onHide,
  onConfirm,
}: AddMissingStrawsDialogProps) => {
  const { t } = useTranslation();

  const [gradingGridData, setGradingGridData] = useState<GradingGridRow[]>(() =>
    defaultGradingRows.map((r) => ({ ...r })),
  );

  const handleConfirm = () => {
    onConfirm(gridToStageGradeCount(gradingGridData));
    setGradingGridData(defaultGradingRows.map((r) => ({ ...r })));
  };

  const handleHide = () => {
    setGradingGridData(defaultGradingRows.map((r) => ({ ...r })));
    onHide();
  };

  const total = sumGradingRows(gradingGridData);

  return (
    <Dialog
      open={visible}
      onOpenChange={(o) => {
        if (!o) handleHide();
      }}
    >
      <DialogContent className="md:w-[580px]">
        <DialogHeader>
          <DialogTitle>
            {t('embryo.addMissingStrawsHeader', 'Add Missing Straws')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <span>
            {t(
              'embryo.addMissingStrawsPrompt',
              'Select the stage and grade for the straws to add to this outside embryo.',
            )}
          </span>
          <GradingGrid
            rowData={gradingGridData}
            storageKey="add-missing-straws-grading-grid"
            onCellValueChanged={(updatedData) =>
              setGradingGridData(updatedData)
            }
          />
          <span className="text-sm text-gray-600">
            {t('embryo.totalStraws', 'Total straws')}: <strong>{total}</strong>
          </span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleHide}>
            <i className="pi pi-times text-xs" />
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={total === 0}>
            <i className="pi pi-check text-xs" />
            {t('common.confirm', 'Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
